import React, { useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { widthPercentageToDP } from "react-native-responsive-screen";
import Carousel, { Pagination } from "react-native-snap-carousel";
import { useDispatch } from "react-redux";
import i18n from "@microdoing/shared/i18n";
import { Box, EmptyJouvie } from "../../../../components/common";
import FullScreenCard from "./FullScreenCard";
import BottomCarousel from "./BottomCarousel";
import { selectStep } from "@microdoing/shared/services/userCampaign/actions";
import theme from "app/Theme/Theme";
import { changeSeeActionAgain } from "@microdoing/shared/services/action/actions";
import { isWeb } from "@microdoing/shared/utils/deviceDetectors";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const SLIDER_WIDTH = widthPercentageToDP("100%");
const ITEM_WIDTH = widthPercentageToDP("100%") - theme.spacing.m * 2;
const DOT_SIZE = 7;

const styles = StyleSheet.create({
  dotStyle: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    backgroundColor: theme.colors["brand.500"],
  },
  dotContainerStyle: {
    marginHorizontal: 4,
  },
  pagination: {
    marginBottom: -5,
    marginTop: -20,
  },
  noPagination: {
    height: 25,
  },
});

const CarouselCards = ({
  navigation,
  recommendedSteps = [],
  onPressAcceptStep,
}) => {
  const carouselRef = useRef<Carousel>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const insets = useSafeAreaInsets();

  const handlePressSocialSheet = item => () => {
    dispatch(selectStep({ stepSelected: item }));
    dispatch(changeSeeActionAgain(false));
    navigation.navigate("Action", {
      isSocialSheetOpen: true,
      isComingFromSelectAction: false,
      cardIndex: activeSlide,
    });
  };

  const handleSeeAction = item => () => {
    dispatch(
      selectStep({
        stepSelected: item,
      }),
    );
    dispatch(changeSeeActionAgain(false));
    navigation.navigate("Action");
  };

  const dispatch = useDispatch();

  const onPressNext = () => {
    if (carouselRef) {
      carouselRef?.current?.snapToItem(activeSlide + 1, true);
    }
  };

  const onPressPrevious = () => {
    if (carouselRef) {
      if (isWeb && activeSlide === 1) {
        return carouselRef?.current?._scrollTo({ offset: 0, animated: true });
      }
      carouselRef?.current?.snapToItem(activeSlide - 1, true);
    }
  };

  const getPagination = () => {
    return (
      <Pagination
        containerStyle={styles.pagination}
        dotsLength={recommendedSteps.length}
        activeDotIndex={activeSlide}
        dotStyle={styles.dotStyle}
        dotContainerStyle={styles.dotContainerStyle}
        inactiveDotOpacity={0.4}
        inactiveDotScale={0.6}
      />
    );
  };

  const renderItem = ({ item, index }) => {
    return (
      <FullScreenCard
        buttonText={i18n.t("accept-action")}
        item={item}
        activeSlide={activeSlide}
        onPress={handleSeeAction(item)}
        onPressSocialSheet={handlePressSocialSheet(item)}
        onPressButton={onPressAcceptStep(item)}
        index={index}
      />
    );
  };

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      backgroundColor="neutral.75"
      flex={1}
      style={{ marginBottom: insets.bottom / 2 }}
    >
      {recommendedSteps.length === 0 && (
        <EmptyJouvie
          firstMessage={i18n.t("no-actions-found")}
          secondMessage={i18n.t("back")}
          onPress={() => navigation.goBack()}
        />
      )}
      <Carousel
        inactiveSlideOpacity={1}
        inactiveSlideScale={1}
        ref={carouselRef}
        scrollEventThrottle={16}
        onScrollIndexChanged={index => setActiveSlide(index)}
        data={recommendedSteps}
        renderItem={renderItem}
        sliderWidth={SLIDER_WIDTH}
        itemWidth={ITEM_WIDTH}
      />
      {getPagination()}
      {recommendedSteps.length === 1 && <View style={styles.noPagination} />}
      <BottomCarousel
        onPressRightArrow={onPressNext}
        onPressLeftArrow={onPressPrevious}
        hasRightArrow={activeSlide < recommendedSteps.length - 1}
        hasLeftArrow={activeSlide > 0}
      />
    </Box>
  );
};

export default CarouselCards;
